import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  RadioGroup, FormControlLabel, Radio, Checkbox, TextField, MenuItem, CircularProgress, alpha,
} from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { useDuplicateClassMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import AppDatePicker from "../../../Components/DatePicker/AppDatePicker";
import PremiumTimePicker from "../../../Components/PremiumTimePicker";
import StaffSearchSelect from "../../../Components/StaffSearchSelect";
import dayjs from "dayjs";

const _toMin = (t: string) => { const [h, m] = String(t || "").split(":").map(Number); return (h || 0) * 60 + (m || 0); };

/**
 * Duplicate a single occurrence to a new date/time, or clone an entire recurring
 * class/series. Optional student copy. The submit button disables while in flight
 * so a double-click cannot double-submit (the backend is also idempotent).
 */
const DuplicateDialog: React.FC<{
  open: boolean;
  row: any;
  staffList: any[];
  onClose: () => void;
  onSuccess?: () => void;
  // Scope the dialog opens with. Default stays "occurrence" so the List
  // page and every other caller keep their existing behaviour. Class
  // Schedule Details passes "series" because that page represents the
  // recurring class/template — the user is far more likely to want a
  // template clone from there than a one-off session copy. The user
  // can still switch to "occurrence" manually inside the dialog.
  defaultScope?: "occurrence" | "series";
}> = ({ open, row, staffList, onClose, onSuccess, defaultScope = "occurrence" }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [duplicateClass, { isLoading }] = useDuplicateClassMutation();

  const [scope, setScope] = useState<"occurrence" | "series">(defaultScope);
  const [copyStudents, setCopyStudents] = useState(true);
  const [targetDate, setTargetDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [staffId, setStaffId] = useState("");

  useEffect(() => {
    if (open && row) {
      setScope(defaultScope);
      setCopyStudents(true);
      setTargetDate(row.scheduledDate ? dayjs(row.scheduledDate).add(7, "day").format("YYYY-MM-DD") : "");
      setStartTime(row.startTime || "");
      setEndTime(row.endTime || "");
      setStaffId(row.staffId ? String(row.staffId) : "");
    }
  }, [open, row]);

  const submit = async () => {
    const payload: any = { scope, copyStudents };
    if (scope === "occurrence") {
      payload.occurrenceId = row.id;
      payload.targetDate = targetDate;
      payload.startTime = startTime;
      payload.endTime = endTime;
      payload.staffId = staffId ? Number(staffId) : undefined;
    } else {
      payload.classId = row.classId;
    }
    try {
      const res = await duplicateClass(payload).unwrap();
      if (res.status) {
        showMessage(res.message);
        onSuccess?.();
        onClose();
      } else showErrorMessage(res.message || "Failed to duplicate");
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to duplicate class");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
        <ContentCopyOutlinedIcon fontSize="small" /> Duplicate Class
      </DialogTitle>
      <DialogContent>
        {row && (
          <Box sx={{ mb: 2.5, p: 2, borderRadius: "10px", bgcolor: alpha("#0f172a", 0.02), border: "1px solid rgba(15,23,42,0.08)" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{row.className || "Class"}</Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{row.staffName || "Unassigned"} · {row.classMode}</Typography>
          </Box>
        )}
        <RadioGroup value={scope} onChange={(e) => setScope(e.target.value as any)} sx={{ mb: 1.5 }}>
          <FormControlLabel value="occurrence" control={<Radio size="small" />} label={
            <Box><Typography sx={{ fontSize: 13, fontWeight: 500 }}>This occurrence only</Typography>
              <Typography sx={{ fontSize: 11, color: "text.secondary" }}>Copy this one session to a new date/time (an "extra" session)</Typography></Box>
          } />
          <FormControlLabel value="series" control={<Radio size="small" />} label={
            <Box><Typography sx={{ fontSize: 13, fontWeight: 500 }}>Entire recurring class/series</Typography>
              <Typography sx={{ fontSize: 11, color: "text.secondary" }}>Clone the class template, weekdays and horizon. No history/attendance/invoices copied</Typography></Box>
          } />
        </RadioGroup>

        {scope === "occurrence" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
            <AppDatePicker label="New Date *" size="small" fullWidth value={targetDate} onChange={setTargetDate} />
            <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
              <PremiumTimePicker label="Start Time" value={startTime}
                onChange={(v) => {
                  const dur = startTime && endTime ? _toMin(endTime) - _toMin(startTime) : 0;
                  setStartTime(v);
                  if (v && dur > 0) { const em = (_toMin(v) + dur) % 1440; setEndTime(`${String(Math.floor(em / 60)).padStart(2, "0")}:${String(em % 60).padStart(2, "0")}`); }
                }} />
              <PremiumTimePicker label="End Time" value={endTime}
                error={!!startTime && !!endTime && _toMin(endTime) <= _toMin(startTime)}
                helperText={startTime && endTime && _toMin(endTime) <= _toMin(startTime) ? "End time must be later than start time." : undefined}
                onChange={(v) => setEndTime(v)} />
            </Box>
            <StaffSearchSelect
              label="Staff"
              value={staffId}
              onChange={setStaffId}
              staff={staffList as any}
              allLabel="Same as original"
              placeholder="Search staff…"
            />
          </Box>
        )}

        <FormControlLabel
          control={<Checkbox size="small" checked={copyStudents} onChange={(e) => setCopyStudents(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 13 }}>Copy enrolled students</Typography>}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: "text.secondary" }}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={isLoading || (scope === "occurrence" && !targetDate)}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <ContentCopyOutlinedIcon />}>
          {isLoading ? "Duplicating..." : "Duplicate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateDialog;
